// C1 (plan 12/09) — « Graphique | Tableau », sur le modèle INSEE.
//
// Un seul geste pour passer d'une représentation à l'autre, au même endroit sur chaque
// GRAPHIQUE : deux boutons pilule accolés, l'actif en marine plein. Il ABSORBE le
// dépliant « Voir les données du graphique » qui vivait sous certains graphiques — deux
// façons d'obtenir le même tableau, c'était une de trop, et celle qui existait était
// invisible (il fallait savoir qu'un `<details>` se cachait là).
//
// ⚠️ SUR LES GRAPHIQUES SEULEMENT. C1 l'avait aussi posé sur chaque tuile de KPI ; le
// retour de la réunion du 13/09 l'en a retiré (W1) — Marc : « il y a marqué graphique et
// tableau alors que ma tuile n'est pas un graphique ». Une tuile porte un chiffre, pas
// une série : il n'y a pas de « vue graphique » à lui opposer. `estRoutePrint` garde donc
// un seul appelant (ChartBlock) ; elle reste définie ici, à côté de la bascule qu'elle
// gouverne, parce que c'est cette bascule qu'elle éteint sur le PDF.
import { useCallback, useEffect, useState } from 'react'

export type VueDonnees = 'graphique' | 'tableau'

const PREFIXE = 'obs2:vue:'

/** La route d'impression (`/o/{insee}/print`), que Chromium headless capture en PDF.
 *
 *  Aucun contrôle interactif n'y a sa place — un bouton « Graphique | Tableau » imprimé sur
 *  du papier est un bouton qu'on ne peut pas presser — et le document doit être
 *  DÉTERMINISTE : il rend la vue par défaut, quoi qu'un lecteur ait pu choisir ailleurs.
 *  Défini ici, à côté de la bascule qu'il gouverne, et consommé aussi bien par ChartBlock
 *  que par les tuiles : deux détections séparées auraient fini par diverger, et la seconde
 *  a justement manqué au premier jet (les tuiles sortaient avec leur bascule dans le PDF). */
export const estRoutePrint = () =>
  typeof window !== 'undefined' && window.location.pathname.endsWith('/print')

// `sessionStorage` et non `localStorage` : le choix vaut pour LA session de lecture
// (« mémorisé par graphique pour la session »), il ne suit pas le lecteur d'un jour à
// l'autre — sinon un élu qui a ouvert un tableau une fois retrouverait des mois plus tard
// un observatoire sans graphiques, sans comprendre pourquoi.
// Chaque accès est gardé : en navigation privée, et sur un navigateur où le stockage de
// site est refusé, la simple LECTURE de `sessionStorage` jette. Un observatoire qui
// s'effondre parce qu'il n'a pas pu se souvenir d'un bouton serait absurde.
function lire(cle: string): VueDonnees {
  try {
    return sessionStorage.getItem(PREFIXE + cle) === 'tableau' ? 'tableau' : 'graphique'
  } catch {
    return 'graphique'
  }
}

/** Mémorise le choix « graphique / tableau » d'UN graphique pour la session.
 *
 *  `cle` identifie le graphique, pas son emplacement : le même graphique gardé en tableau
 *  reste en tableau quand le lecteur change de commune — c'est une préférence de lecture,
 *  elle ne se remet pas à zéro à chaque navigation.
 */
export function useVueDonnees(cle: string, memoriser = true): [VueDonnees, (v: VueDonnees) => void] {
  // `memoriser = false` (page d'un lien partagé, CDC E2 § 9) : rien n'est lu ni écrit sur le
  // terminal du visiteur — le choix vit le temps de la page.
  const [vue, setVue] = useState<VueDonnees>(() => (memoriser ? lire(cle) : 'graphique'))

  // Quand la clé change, le composant regarde un AUTRE graphique : il doit s'ouvrir sur le
  // choix mémorisé pour celui-là. Sans cette relecture, React réutilise l'instance montée
  // et le nouveau graphique héritait de l'état du précédent.
  useEffect(() => { if (memoriser) setVue(lire(cle)) }, [cle, memoriser])

  const choisir = useCallback((v: VueDonnees) => {
    setVue(v)
    if (!memoriser) return
    try { sessionStorage.setItem(PREFIXE + cle, v) } catch { /* stockage refusé : sans mémoire */ }
  }, [cle, memoriser])

  return [vue, choisir]
}

/** Le bouton segmenté lui-même. `nom` sert le nom accessible : sur une page qui porte
 *  quinze de ces bascules, « Graphique / Tableau » répété quinze fois ne dit à un lecteur
 *  d'écran de QUOI on parle. */
export function BasculeVue({ vue, onChange, nom }: {
  vue: VueDonnees; onChange: (v: VueDonnees) => void; nom?: string
}) {
  return (
    <div className="obs2-bascule" role="group"
         aria-label={nom ? `Affichage de « ${nom} » : graphique ou tableau`
                         : 'Affichage : graphique ou tableau'}>
      {([['graphique', 'Graphique'], ['tableau', 'Tableau']] as const).map(([id, lab]) => (
        <button key={id} type="button" aria-pressed={vue === id}
                className={vue === id ? 'actif' : undefined}
                onClick={() => onChange(id)}>
          {lab}
        </button>
      ))}
    </div>
  )
}
